<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { vOnClickOutside } from '@vueuse/components';
import { useWindowSize } from '@vueuse/core';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import ToggleCopilotAssistant from 'dashboard/components-next/copilot/ToggleCopilotAssistant.vue';
import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';
import wootConstants from 'dashboard/constants/globals';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import {
  getLilithAssistants,
  getTeamThread,
  sendTeamMessage,
} from 'dashboard/api/lilithTeam';
import { insertTeamReply } from './teamReply';
import { selectTeamAssistant } from './teamAssistantSelection';
import {
  mergeTeamMessages,
  realtimeMessagesForThread,
  resolveRealtimeThreadRef,
} from './teamRealtime';
import {
  LILITH_CONVERSATION_AI_FEATURE,
  isLilithTeamAssistAvailable,
  isLilithTeamAssistConversationRoute,
} from 'dashboard/routes/dashboard/lilith/lilithSurfaces';

const route = useRoute();
const currentChat = useMapGetter('getSelectedChat');
const lastPublicMessage = useMapGetter('getLastEmailInSelectedChat');
const currentAccountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');
const copilotRealtimeRecords = useMapGetter('copilotMessages/getRecords');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();

const messages = ref([]);
const assistants = ref([]);
const selectedThreadRef = ref(null);
let pendingSend = null;
const input = ref('');
const loading = ref(false);
const sending = ref(false);
let loadGeneration = 0;
let sendStartedAt = 0;

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);
const isTeamLilithAvailable = computed(() =>
  isLilithTeamAssistAvailable({
    accountEntitled: isFeatureEnabledonAccount.value(
      currentAccountId.value,
      LILITH_CONVERSATION_AI_FEATURE
    ),
  })
);
const conversationId = computed(() =>
  isLilithTeamAssistConversationRoute(route.name)
    ? currentChat.value?.id || null
    : null
);
const hasConversationContext = computed(() => Boolean(conversationId.value));
const canSuggestReply = computed(
  () => lastPublicMessage.value?.message_type === MESSAGE_TYPE.INCOMING
);
const isOpen = computed(() =>
  Boolean(isTeamLilithAvailable.value && uiSettings.value.is_copilot_panel_open)
);
const channelType = computed(() =>
  hasConversationContext.value ? currentChat.value?.meta?.channel || '' : ''
);
const activeAssistant = computed(() =>
  selectTeamAssistant({
    assistants: assistants.value,
    preferredAssistantId: uiSettings.value.preferred_captain_assistant_id,
    inboxId: hasConversationContext.value ? currentChat.value?.inbox_id : null,
  })
);
const canAsk = computed(() =>
  Boolean(input.value.trim() && activeAssistant.value?.id && !sending.value)
);
const hasMessages = computed(() => messages.value.length > 0);
const sidebarButtons = computed(() =>
  hasMessages.value
    ? [
        {
          key: 'reset',
          icon: 'i-lucide-refresh-ccw',
          tooltip: 'CAPTAIN.COPILOT.RESET',
        },
      ]
    : []
);
const groupedMessages = computed(() => {
  const result = [];
  let thinking = [];
  const flushThinking = () => {
    if (!thinking.length) return;
    result.push({
      id: thinking[0].id,
      role: 'thinking_group',
      messages: thinking,
    });
    thinking = [];
  };
  messages.value.forEach(message => {
    if (message.role === 'assistant_thinking') {
      thinking.push(message);
      return;
    }
    flushThinking();
    result.push(message);
  });
  flushThinking();
  return result;
});
const closePanel = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
  });
};

const closeOnSmallScreen = () => {
  if (isSmallScreen.value && isOpen.value) closePanel();
};

const resetThread = (discardPending = false) => {
  if (discardPending && pendingSend?.storageKey)
    sessionStorage.removeItem(pendingSend.storageKey);
  pendingSend = null;
  loadGeneration += 1;
  selectedThreadRef.value = null;
  messages.value = [];
};

const handleSidebarAction = action => {
  if (action === 'reset') resetThread(true);
};

const loadAssistants = async () => {
  if (!currentAccountId.value) {
    assistants.value = [];
    return;
  }
  try {
    const response = await getLilithAssistants(currentAccountId.value);
    assistants.value = Array.isArray(response.data?.payload)
      ? response.data.payload
      : [];
  } catch (error) {
    assistants.value = [];
    useAlert(error.message);
  }
};

const setAssistant = async assistant => {
  if (!assistant?.id) return;
  resetThread(true);
  await updateUISettings({
    preferred_captain_assistant_id: assistant.id,
  });
};

const loadThread = async () => {
  const accountId = currentAccountId.value;
  const threadRef = selectedThreadRef.value;
  if (!accountId || !threadRef) {
    messages.value = [];
    return;
  }
  loadGeneration += 1;
  const generation = loadGeneration;
  loading.value = true;
  try {
    const response = await getTeamThread(accountId, threadRef);
    if (
      generation === loadGeneration &&
      currentAccountId.value === accountId &&
      selectedThreadRef.value === threadRef
    ) {
      messages.value = Array.isArray(response.data?.messages)
        ? response.data.messages
        : [];
    }
  } catch (error) {
    if (generation === loadGeneration) useAlert(error.message);
  } finally {
    if (generation === loadGeneration) loading.value = false;
  }
};

const send = async (message, requestType = 'chat') => {
  const userId = currentUser.value?.id;
  const text = String(message || '').trim();
  const scopedConversationId = conversationId.value;
  if (!text || !currentAccountId.value || sending.value) return;
  if (requestType === 'reply_suggestion' && !scopedConversationId) return;
  if (!activeAssistant.value?.id) return;

  sending.value = true;
  sendStartedAt = Date.now();
  const accountId = currentAccountId.value;
  const assistantId = activeAssistant.value.id;
  let operation;
  try {
    const storageKey = `lilith-team-pending:${accountId}:${userId}:${scopedConversationId || 'general'}`;
    const identity = JSON.stringify([
      accountId,
      userId,
      scopedConversationId,
      text,
      requestType,
      assistantId,
    ]);
    const digest = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(identity)
    );
    if (
      currentAccountId.value !== accountId ||
      currentUser.value?.id !== userId ||
      conversationId.value !== scopedConversationId ||
      activeAssistant.value?.id !== assistantId
    )
      return;
    const signature = Array.from(new Uint8Array(digest), byte =>
      byte.toString(16).padStart(2, '0')
    ).join('');
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
    } catch {
      sessionStorage.removeItem(storageKey);
    }
    if (!pendingSend || pendingSend.signature !== signature) {
      pendingSend =
        saved?.signature === signature &&
        saved?.storageKey === storageKey &&
        /^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(
          saved?.requestRef || ''
        )
          ? saved
          : {
              signature,
              requestRef: crypto.randomUUID(),
              threadRef: selectedThreadRef.value,
              storageKey,
            };
    }
    operation = pendingSend;
    sessionStorage.setItem(storageKey, JSON.stringify(operation));
    const response = await sendTeamMessage(
      accountId,
      scopedConversationId,
      text,
      requestType,
      operation.threadRef,
      assistantId,
      operation.requestRef
    );
    const currentOperation = pendingSend === operation;
    if (currentOperation) {
      sessionStorage.removeItem(operation.storageKey);
      pendingSend = null;
    }
    if (
      currentOperation &&
      conversationId.value === scopedConversationId &&
      currentAccountId.value === accountId
    ) {
      selectedThreadRef.value =
        response.data?.thread_ref || selectedThreadRef.value;
      input.value = '';
      await loadThread();
    }
  } catch (error) {
    if (!operation || pendingSend === operation) useAlert(error.message);
  } finally {
    sending.value = false;
    sendStartedAt = 0;
  }
};

const useReply = message => {
  if (insertTeamReply(message.content, channelType.value)) closeOnSmallScreen();
};

watch(
  copilotRealtimeRecords,
  records => {
    const available = Array.isArray(records) ? records : [];
    const resolvedThreadRef = resolveRealtimeThreadRef({
      records: available,
      selectedThreadRef: selectedThreadRef.value,
      conversationId: conversationId.value,
      sending: sending.value,
      sendStartedAt,
    });
    if (!resolvedThreadRef) return;
    if (!selectedThreadRef.value) selectedThreadRef.value = resolvedThreadRef;
    if (selectedThreadRef.value !== resolvedThreadRef) return;
    messages.value = mergeTeamMessages(
      messages.value,
      realtimeMessagesForThread(available, resolvedThreadRef)
    );
  },
  { deep: true }
);

watch(
  [isOpen, conversationId],
  ([open, scopedConversationId], [, previousConversationId]) => {
    if (scopedConversationId !== previousConversationId) {
      resetThread();
    }
    if (open && selectedThreadRef.value) loadThread();
    else if (!open || !selectedThreadRef.value) messages.value = [];
  },
  { immediate: true }
);

watch(
  [isOpen, currentAccountId],
  ([open, accountId]) => {
    if (open && accountId) loadAssistants();
  },
  { immediate: true }
);
</script>

<template>
  <div
    v-show="isOpen"
    v-on-click-outside="[
      closeOnSmallScreen,
      {
        ignore: [
          'dialog.ProseMirror-prompt-backdrop',
          '[data-popover-content]',
          '[data-popover-backdrop]',
        ],
      },
    ]"
    class="bg-n-surface-2 h-full overflow-hidden flex flex-col fixed top-0 ltr:right-0 rtl:left-0 z-40 w-full max-w-sm md:static md:w-[320px] md:min-w-[320px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg md:shadow-none"
  >
    <SidebarActionsHeader
      :title="$t('TEAM_LILITH.TITLE')"
      :buttons="sidebarButtons"
      @click="handleSidebarAction"
      @close="closePanel"
    />

    <div
      class="flex items-center justify-between gap-2 px-3 py-2 border-b border-n-weak"
    >
      <div class="flex min-w-0 items-center gap-2">
        <ToggleCopilotAssistant
          v-if="assistants.length"
          :assistants="assistants"
          :active-assistant="activeAssistant"
          @set-assistant="setAssistant"
        />
        <span
          v-if="hasConversationContext"
          class="truncate text-xs text-n-slate-10"
        >
          {{ $t('TEAM_LILITH.CONVERSATION', { id: conversationId }) }}
        </span>
      </div>
    </div>

    <div class="flex-1 overflow-y-auto px-3 py-4">
      <div
        v-if="loading && messages.length === 0"
        class="text-sm text-n-slate-10"
      >
        {{ $t('TEAM_LILITH.LOADING') }}
      </div>
      <div
        v-else-if="messages.length === 0"
        class="flex flex-col gap-3 text-sm text-n-slate-10"
      >
        <span>{{ $t('TEAM_LILITH.EMPTY') }}</span>
        <div
          v-if="hasConversationContext && activeAssistant"
          class="flex flex-col gap-2"
        >
          <span class="text-xs">
            {{ $t('CAPTAIN.COPILOT.TRY_THESE_PROMPTS') }}
          </span>
          <Button
            :label="$t('CAPTAIN.COPILOT.PROMPTS.SUMMARIZE.LABEL')"
            faded
            slate
            sm
            @click="send($t('CAPTAIN.COPILOT.PROMPTS.SUMMARIZE.CONTENT'))"
          />
          <Button
            v-if="canSuggestReply"
            :label="$t('CAPTAIN.COPILOT.PROMPTS.SUGGEST.LABEL')"
            faded
            slate
            sm
            @click="
              send(
                $t('CAPTAIN.COPILOT.PROMPTS.SUGGEST.CONTENT'),
                'reply_suggestion'
              )
            "
          />
          <Button
            :label="$t('CAPTAIN.COPILOT.PROMPTS.RATE.LABEL')"
            faded
            slate
            sm
            @click="send($t('CAPTAIN.COPILOT.PROMPTS.RATE.CONTENT'))"
          />
        </div>
        <div v-else-if="activeAssistant" class="flex flex-col gap-2">
          <span class="text-xs">
            {{ $t('CAPTAIN.COPILOT.TRY_THESE_PROMPTS') }}
          </span>
          <Button
            :label="$t('CAPTAIN.COPILOT.PROMPTS.HIGH_PRIORITY.LABEL')"
            faded
            slate
            sm
            @click="send($t('CAPTAIN.COPILOT.PROMPTS.HIGH_PRIORITY.CONTENT'))"
          />
          <Button
            :label="$t('CAPTAIN.COPILOT.PROMPTS.LIST_CONTACTS.LABEL')"
            faded
            slate
            sm
            @click="send($t('CAPTAIN.COPILOT.PROMPTS.LIST_CONTACTS.CONTENT'))"
          />
        </div>
      </div>
      <div v-else class="space-y-4">
        <div
          v-for="message in groupedMessages"
          :key="message.id"
          class="flex flex-col gap-1"
          :class="message.role === 'user' ? 'items-end' : 'items-start'"
        >
          <template v-if="message.role === 'thinking_group'">
            <details open class="w-full rounded-lg bg-n-alpha-1 px-3 py-2">
              <summary
                class="cursor-pointer text-xs font-medium text-n-slate-10"
              >
                {{
                  $t('CAPTAIN.COPILOT.SHOW_STEPS', {
                    count: message.messages.length,
                  })
                }}
              </summary>
              <div class="mt-2 space-y-1">
                <div
                  v-for="step in message.messages"
                  :key="step.id"
                  class="text-xs text-n-slate-11"
                >
                  {{ step.content }}
                </div>
              </div>
            </details>
          </template>
          <template v-else>
            <div class="text-xs font-medium text-n-slate-10">
              {{
                message.role === 'user'
                  ? $t('TEAM_LILITH.YOU')
                  : $t('TEAM_LILITH.LILITH')
              }}
            </div>
            <div
              class="max-w-full whitespace-pre-wrap break-words rounded-lg px-3 py-2 text-sm text-n-slate-12"
              :class="message.role === 'user' ? 'bg-n-alpha-2' : 'bg-n-alpha-1'"
            >
              {{ message.content }}
            </div>
            <Button
              v-if="message.role === 'assistant' && message.reply_suggestion"
              :label="$t('TEAM_LILITH.USE_REPLY')"
              faded
              sm
              slate
              @click="useReply(message)"
            />
          </template>
        </div>
      </div>
    </div>

    <form class="border-t border-n-weak p-3" @submit.prevent="send(input)">
      <textarea
        v-model="input"
        rows="2"
        maxlength="20000"
        :placeholder="$t('TEAM_LILITH.PLACEHOLDER')"
        class="w-full reset-base resize-none rounded-lg border border-n-weak bg-n-alpha-3 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-11"
        @keydown.enter.exact.prevent="send(input)"
      />
      <div class="mt-2 flex justify-end">
        <Button
          :label="$t('TEAM_LILITH.ASK')"
          sm
          :disabled="!canAsk"
          :is-loading="sending"
          type="submit"
        />
      </div>
    </form>
  </div>
</template>
